import { useEffect, useLayoutEffect, useRef, useState } from "react";

export type DocumentTabContextAction =
  | "close"
  | "closeOthers"
  | "closeLeft"
  | "closeRight"
  | "closeAll"
  | "rename"
  | "newDocument";

interface DocumentTabContextMenuProps {
  x: number;
  y: number;
  canCloseOthers: boolean;
  canCloseLeft: boolean;
  canCloseRight: boolean;
  onAction: (action: DocumentTabContextAction) => void;
  onClose: () => void;
}

export function DocumentTabContextMenu({
  x,
  y,
  canCloseOthers,
  canCloseLeft,
  canCloseRight,
  onAction,
  onClose,
}: DocumentTabContextMenuProps) {
  const menuRef = useRef<HTMLDivElement | null>(null);
  const [position, setPosition] = useState(() => ({ left: x, top: y }));

  useLayoutEffect(() => {
    function updatePosition(): void {
      const menu = menuRef.current;
      const margin = 8;
      const width = menu?.offsetWidth ?? 200;
      const height = menu?.offsetHeight ?? 220;
      setPosition({
        left: Math.min(Math.max(margin, x), Math.max(margin, window.innerWidth - width - margin)),
        top: Math.min(Math.max(margin, y), Math.max(margin, window.innerHeight - height - margin)),
      });
    }

    updatePosition();
    window.addEventListener("resize", updatePosition);
    return () => window.removeEventListener("resize", updatePosition);
  }, [x, y]);

  useEffect(() => {
    const handlePointerDown = (event: PointerEvent) => {
      if (menuRef.current?.contains(event.target as Node)) {
        return;
      }
      onClose();
    };
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        onClose();
      }
    };
    window.addEventListener("pointerdown", handlePointerDown, true);
    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("pointerdown", handlePointerDown, true);
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [onClose]);

  function run(action: DocumentTabContextAction): void {
    onAction(action);
    onClose();
  }

  return (
    <div
      ref={menuRef}
      className="fixed z-50 min-w-[11rem] overflow-hidden rounded-md border border-white/10 bg-[#242a31] p-1 text-[12px] text-slate-200 shadow-xl shadow-black/30"
      style={{ left: position.left, top: position.top }}
      role="menu"
    >
      <MenuButton label="New Document" onClick={() => run("newDocument")} />
      <MenuButton label="Rename" onClick={() => run("rename")} />
      <div className="my-1 h-px bg-white/10" />
      <MenuButton label="Close" onClick={() => run("close")} />
      <MenuButton label="Close Others" disabled={!canCloseOthers} onClick={() => run("closeOthers")} />
      <MenuButton label="Close to the Left" disabled={!canCloseLeft} onClick={() => run("closeLeft")} />
      <MenuButton label="Close to the Right" disabled={!canCloseRight} onClick={() => run("closeRight")} />
      <MenuButton label="Close All" onClick={() => run("closeAll")} />
    </div>
  );
}

function MenuButton({
  label,
  disabled,
  onClick,
}: {
  label: string;
  disabled?: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      role="menuitem"
      disabled={disabled}
      className="h-8 w-full rounded px-2 text-left hover:bg-white/[0.08] disabled:cursor-default disabled:text-slate-600 disabled:hover:bg-transparent"
      onClick={onClick}
    >
      {label}
    </button>
  );
}
